'use client'

import { ChevronsUpDown } from 'lucide-react'
import { useState } from 'react'

import { cn } from '@/lib/cn'
import type { Option } from '@/types/option'

import { Button } from '../button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
} from '../command'
import { Popover, PopoverContent, PopoverTrigger } from '../popover'
import { Skeleton } from '../skeleton'

type ComboboxProps<TItem extends object = object> = {
  search: string
  handleSearch: (search: string) => void
  items: (Option & TItem)[]
  selected?: Option & TItem
  handleSelect: (item: Option & TItem) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  loading?: boolean
  isError?: boolean
}

const Combobox = <TItem extends object = object>({
  search,
  handleSearch,
  items,
  selected,
  handleSelect,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  loading = false,
  isError = false,
}: ComboboxProps<TItem>) => {
  const [open, setOpen] = useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between text-system-text-primary',
            isError && 'border border-system-state-error',
            !selected && 'text-system-text-secondary',
          )}
        >
          {selected?.label
            ? selected.label
            : (placeholder ?? 'Selecione um item')}
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="max-h-[--radix-popover-content-available-height] w-[--radix-popover-trigger-width] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            value={search}
            onValueChange={(value) => handleSearch(value)}
            placeholder={searchPlaceholder ?? 'Buscar'}
          />
          <CommandList>
            {!loading && !items.length && (
              <CommandEmpty>
                {emptyMessage ?? 'Nenhum registro encontrado'}
              </CommandEmpty>
            )}

            {loading ? (
              <CommandLoading>
                <div className="flex flex-col gap-2">
                  <Skeleton className="h-9 w-full" />
                  <Skeleton className="h-9 w-full" />
                </div>
              </CommandLoading>
            ) : (
              <CommandGroup>
                {items.map(
                  (item: Option & TItem & { area?: string | null }) => (
                    <CommandItem
                      key={item.value}
                      value={item.value}
                      onSelect={(currentValue: string) => {
                        const selectedItem = items.find(
                          ({ value }) => value === currentValue,
                        )
                        if (selectedItem) handleSelect(selectedItem)
                        setOpen(false)
                      }}
                      className="flex cursor-pointer flex-col items-start gap-2"
                    >
                      {/* <Check
                      className={cn(
                        'mr-2 size-4',
                        selected?.value === item.value
                          ? 'opacity-100'
                          : 'opacity-0',
                      )}
                    /> */}
                      {item.label}
                      {item.area && (
                        <span className="text-xs text-system-text-secondary">
                          {item.area}
                        </span>
                      )}
                    </CommandItem>
                  ),
                )}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
Combobox.displayName = 'Combobox'

export { Combobox, type ComboboxProps }
